import type { HostLocale } from "@vidimo/shared";
import {
  createTranslator,
  type NamespaceKeys,
  type NestedKeyOf,
} from "use-intl";
import type { InterfaceMessages } from "./catalogs";
import { createIntlPolicy, intlPolicyMode } from "./intl-policy";
import { getInterfaceLocaleState } from "./locale-store";

type InterfaceNamespace = NamespaceKeys<
  InterfaceMessages,
  NestedKeyOf<InterfaceMessages>
>;

const { getMessageFallback, onError } = createIntlPolicy(intlPolicyMode());

function translatorFor<const N extends InterfaceNamespace>(
  locale: HostLocale,
  messages: InterfaceMessages,
  namespace: N
) {
  return createTranslator({
    getMessageFallback,
    locale,
    messages,
    namespace,
    onError,
  });
}

/**
 * One translator per catalog and namespace, so its parsed messages are reused
 * across calls. Keyed by the catalog object, which changes with the language.
 */
const translators = new WeakMap<InterfaceMessages, Map<string, unknown>>();

/**
 * A translator for words outside React (a toast a hook raises, an error a
 * module throws to the screen), in the interface language on screen when it
 * is called. Call it where the words are needed, never at module load: the
 * language can change after.
 */
export function interfaceTranslator<const N extends InterfaceNamespace>(
  namespace: N
): ReturnType<typeof translatorFor<N>> {
  const { locale, messages } = getInterfaceLocaleState();
  let byNamespace = translators.get(messages);
  if (!byNamespace) {
    byNamespace = new Map();
    translators.set(messages, byNamespace);
  }
  const key = `${locale}:${namespace}`;
  const cached = byNamespace.get(key) as
    | ReturnType<typeof translatorFor<N>>
    | undefined;
  if (cached) {
    return cached;
  }
  const translator = translatorFor(locale, messages, namespace);
  byNamespace.set(key, translator);
  return translator;
}
